import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, X } from "lucide-react";

/**
 * Camera-based barcode scanner modal. Uses html5-qrcode.
 * Calls onScan(code) once per detected barcode, then user can keep scanning.
 */
export default function CameraScanner({ open, onScan, onClose }) {
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);
  const scannerRef = useRef(null);
  const containerId = "camera-scanner-region";
  const lastCodeRef = useRef({ code: "", time: 0 });
  // Keep the latest onScan callback in a ref so the effect below doesn't
  // depend on it — parents often pass inline arrow functions which changed
  // identity every render and caused the scanner to restart mid-stream
  // (Html5Qrcode threw "already started / cannot stop" errors).
  const onScanRef = useRef(onScan);
  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError("");
    setReady(false);

    const start = async () => {
      try {
        const html5 = new Html5Qrcode(containerId, { verbose: false });
        if (cancelled) return;
        scannerRef.current = html5;
        await html5.start(
          { facingMode: "environment" },
          {
            fps: 15,
            qrbox: { width: 260, height: 160 },
            aspectRatio: 1.6,
          },
          (decodedText) => {
            const now = Date.now();
            if (
              lastCodeRef.current.code === decodedText &&
              now - lastCodeRef.current.time < 1500
            )
              return;
            lastCodeRef.current = { code: decodedText, time: now };
            onScanRef.current?.(decodedText);
          },
          () => {},
        );
        if (cancelled) {
          try {
            await html5.stop();
            html5.clear();
          } catch (_) {
            /* ignore */
          }
          return;
        }
        setReady(true);
      } catch (e) {
        if (cancelled) return;
        setError(
          e?.message ||
            "Kameraya erişilemedi. Tarayıcı izinlerini kontrol edin.",
        );
      }
    };
    start();

    return () => {
      cancelled = true;
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) {
        // Html5Qrcode.stop() throws if the scanner isn't actually running;
        // catch it silently so React doesn't surface it as a runtime error.
        try {
          const p = s.stop();
          if (p && typeof p.then === "function") {
            p.then(() => {
              try {
                s.clear();
              } catch (_) {
                /* ignore */
              }
            }).catch(() => {});
          }
        } catch (_) {
          /* ignore */
        }
      }
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white hard border-2 border-[color:var(--border)] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-[color:var(--border)]">
          <div className="flex items-center gap-2">
            <Camera size={18} className="text-[color:var(--accent)]" />
            <div>
              <div className="ticker text-[10px] text-[color:var(--text-3)]">
                /// KAMERA
              </div>
              <div className="font-bold">Barkod Okut</div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="camera-scanner-close"
            className="p-2 hard hover:bg-[color:var(--bg)]"
          >
            <X size={18} />
          </button>
        </div>
        <div
          id={containerId}
          className="bg-black w-full aspect-video"
          data-testid="camera-scanner-region"
        />
        <div className="p-4 space-y-2 text-sm">
          {error ? (
            <div className="hard border border-[color:var(--error)] bg-[color:var(--error-bg)] text-[color:var(--error)] p-2">
              {error}
            </div>
          ) : (
            <div className="text-[color:var(--text-2)]">
              {ready
                ? "Barkodu kutunun içine hizalayın. Otomatik okunacak."
                : "Kamera başlatılıyor…"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
